import axios from "axios";
import beveatApiResourceOwner from "../../../api/resource_owner_beveat_api.js";
import beveatApiStaging from "../../../api/staging_beveat_api.js";
import beveatApiSandbox from "../../../api/sandbox_beveat_api.js";
import beveatApiProduction from "../../../api/production_beveat_api.js";
import { InfoCircle } from "iconsax-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  CertificateSyscodeMap
} from "../../../constants/certificate_syscode.jsx";
import { commonConfigs, environmentHeaders, environmentUrls, urls } from "../../../../config.js";
import { INVOICE_PLAN_SYSCODE, InvoicePlanSyscodeString } from "../../../constants/invoice_plan_syscode.jsx";
import { getUser } from "../../../services/local_service.js";
import { DEVELOPER_CERTIFICATION_STATUS } from "../../../constants/developer_certification_status.jsx";
import { TickCircle, CloseCircle, } from "iconsax-react";

const errorIcon = (
  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
    <InfoCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
  </div>
);

export default function DeveloperCertificationForm({
  tab,
  certificateData,
  environment,
}) {
  const [isDisabled, setIsDisabled] = useState(false);
  const certificateSyscode = CertificateSyscodeMap[environment]?.[tab.value];
  const accountId = getUser()?.account_id;
  const certData = certificateData;
  certificateData = certData.find(
    (certificate) => certificate.certificate_syscode === certificateSyscode
  );
  const invoicePlanSyscode = Object.values(InvoicePlanSyscodeString).indexOf(environment);

  // Determine certification status and button states
  const certificationStatus = certificateData?.developer_certification_status;
  const hasTransactionData = certificateData?.txn_id && certificateData?.txn_retrieval_ref;

  const isReleased = certificationStatus === DEVELOPER_CERTIFICATION_STATUS.Released
  const isCertified = certificationStatus === DEVELOPER_CERTIFICATION_STATUS.Certified;;
  const isPending = certificationStatus === DEVELOPER_CERTIFICATION_STATUS.Pending && hasTransactionData;
  const canCertify = !hasTransactionData;

  const txnIDs = certificateData?.txn_id.split(",");
  const retRefNum = certificateData?.txn_retrieval_ref.split(",");

  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    reset,
  } = useForm({
    defaultValues: {
      txn_id: txnIDs ? txnIDs[0] : null,
      txn_retrieval_ref: retRefNum ? retRefNum[0] : null,
      txn_id_timeout: txnIDs ? txnIDs[1] : null,
      txn_retrieval_ref_timeout: retRefNum ? retRefNum[1] : null,
      // 6 extra fields for eNETS 
      txn_id_2: txnIDs ? txnIDs[2] : null,
      txn_retrieval_ref_2: retRefNum ? retRefNum[2] : null,
      txn_id_3: txnIDs ? txnIDs[3] : null,
      txn_retrieval_ref_3: retRefNum ? retRefNum[3] : null,
      txn_id_timeout_2: txnIDs ? txnIDs[4] : null,
      txn_retrieval_ref_timeout_2: retRefNum ? retRefNum[4] : null,
    },
  });

  let certifyUrl =
    urls.resourceOwnerUrl +
    commonConfigs.apiUrls.updateCertificate();

  function formatData(data) {
    var newData = data;
    newData.certificate_syscode = certificateSyscode;
    newData.developer_email = certData[0].developer_email;
    newData.developer_name = certData[0].developer_name;
    newData.org_invoice_plan_id = certData[0].org_invoice_plan_id;
    newData.developer_proj_id = certData[0].developer_proj_id;
    newData.account_id = accountId;
    newData.invoice_plan_syscode = invoicePlanSyscode;
    // console.log("formatdata", newData);
    return newData;
  }

  async function validateNets(retrievalRef, txnId) {
    const url = environmentUrls[environment]
    const header = environmentHeaders[environment]
    let apiUrls;
    if (environment === InvoicePlanSyscodeString[INVOICE_PLAN_SYSCODE.Sandbox]) {
      apiUrls = {
        0: commonConfigs.apiUrls.validateNetsQrTxn,
        1: commonConfigs.apiUrls.validateNetsQrTxn,
        2: commonConfigs.apiUrls.validateEnetsTxn,
      };
    } else {
      apiUrls = {
        0: commonConfigs.apiUrls.validateNetsClickTxn,
        1: commonConfigs.apiUrls.validateNetsQrTxn,
        2: commonConfigs.apiUrls.validateNetsQrTxn,
        3: commonConfigs.apiUrls.validateEnetsTxn,
        4: commonConfigs.apiUrls.validateEnetsTxn,
      };
    }

    const apiUrl = apiUrls[tab.value];
    const platform = tab.name.includes("Web") ? 0 : tab.name.includes("Mobile") ? 1 : null;

    let validateNetsUrl = url + apiUrl(retrievalRef, txnId, platform);
    if (retrievalRef.length > 1) {
      validateNetsUrl = url + apiUrl(retrievalRef[0], txnId[0], platform);
      for (let i = 1; i < retrievalRef.length; i++) {
        validateNetsUrl += `&txn_retrieval_ref=${retrievalRef[i]}&txn_id=${txnId[i]}`
      }
    }

    let apiInstance;

    if (environment === InvoicePlanSyscodeString[INVOICE_PLAN_SYSCODE.Sandbox]) {
      apiInstance = beveatApiSandbox;
    } else if (environment === InvoicePlanSyscodeString[INVOICE_PLAN_SYSCODE.Staging]) {
      apiInstance = beveatApiStaging;
    } else if (environment === InvoicePlanSyscodeString[INVOICE_PLAN_SYSCODE.Production]) {
      apiInstance = beveatApiProduction;
    } else {
      apiInstance = beveatApiResourceOwner;
    }

    return await apiInstance
      // change special character '+' to '%2B'
      .get(validateNetsUrl.replace(new RegExp('\\+', 'g'), '%2B'), { headers: header })
      .then((res) => {
        console.log(res.data.result.data);
        return res.data.result.data;
      })
      .catch((err) => {
        console.log(err);
        return null;
      });
  }

  const onSubmit = async (data) => {
    setIsDisabled(true); // Disable the submit button

    // Transaction ID validation for different environments
    console.log("Environment:", environment);
    console.log("Tab name:", tab.name);

    let expectedTransactionId = null;

    // Sandbox environment validation
    if (environment === InvoicePlanSyscodeString[INVOICE_PLAN_SYSCODE.Sandbox]) {
      console.log("Sandbox validation");
      if (tab.name === "NETS QR (Web)") {
        expectedTransactionId = process.env.REACT_APP_SANDBOX_NETS_QR_WEB_TXN_ID;
      } else if (tab.name === "NETS QR (Mobile)") {
        expectedTransactionId = process.env.REACT_APP_SANDBOX_NETS_QR_MOBILE_TXN_ID;
      } else if (tab.name === "eNETS (Web)") {
        expectedTransactionId = process.env.REACT_APP_SANDBOX_ENETS_WEB_TXN_ID;
      }
    }
    // Specific check for staging environment to not let user use exposed txn_id from coursework
    if (
      environment === InvoicePlanSyscodeString[INVOICE_PLAN_SYSCODE.Staging] &&
      data.txn_id === process.env.REACT_APP_STAGING_NETS_QR_WEB_TXN_ID
    ) {
      alert(`This transaction ID is not allowed for Staging environment.`);
      setIsDisabled(false);
      return;
    }

    // If env variable is set and user transaction ID doesn't match, show error
    if (expectedTransactionId && expectedTransactionId.trim() !== '' && data.txn_id !== expectedTransactionId) {
      alert(`Invalid transaction ID for this platform type certification.`);
      setIsDisabled(false);
      return;
    }

    // Check if txn_retrieval_ref contains any excluded values (only for Sandbox and UAT, not Production)
    if (environment === InvoicePlanSyscodeString[INVOICE_PLAN_SYSCODE.Sandbox] ||
      environment === InvoicePlanSyscodeString[INVOICE_PLAN_SYSCODE.Staging]) {

      const excludedRetrievalRefs = process.env.REACT_APP_EXCLUDE_NETS_TXN_RETRIEVAL_REF;
      if (excludedRetrievalRefs && excludedRetrievalRefs.trim() !== '') {
        const excludedValues = excludedRetrievalRefs.split('|');
        const userRetrievalRef = data.txn_retrieval_ref || '';

        const containsExcludedValue = excludedValues.some(excludedValue =>
          userRetrievalRef.includes(excludedValue.trim())
        );

        if (containsExcludedValue) {
          alert(`Invalid transaction retrieval reference number.`);
          setIsDisabled(false);
          return;
        }
      }
    }

    var newData = formatData(data);
    let retRefArr = [newData.txn_retrieval_ref];
    let txnIdArr = [newData.txn_id];
    
    if (tab.name.includes("eNETS")) {
      // Success pairs: 1, 2, 3
      newData.txn_id_2 && txnIdArr.push(newData.txn_id_2);
      newData.txn_retrieval_ref_2 && retRefArr.push(newData.txn_retrieval_ref_2);
      newData.txn_id_3 && txnIdArr.push(newData.txn_id_3);
      newData.txn_retrieval_ref_3 && retRefArr.push(newData.txn_retrieval_ref_3);
      // Timeout pairs: 1, 2
      newData.txn_id_timeout && txnIdArr.push(newData.txn_id_timeout);
      newData.txn_retrieval_ref_timeout && retRefArr.push(newData.txn_retrieval_ref_timeout);
      newData.txn_id_timeout_2 && txnIdArr.push(newData.txn_id_timeout_2);
      newData.txn_retrieval_ref_timeout_2 && retRefArr.push(newData.txn_retrieval_ref_timeout_2);
    } else if (tab.name.includes("NETS QR")) {
      newData.txn_retrieval_ref_timeout && retRefArr.push(newData.txn_retrieval_ref_timeout);
      newData.txn_id_timeout && txnIdArr.push(newData.txn_id_timeout);
    }
    // console.log("formatted data", newData);
    const response = await validateNets(
      retRefArr,
      txnIdArr
    );
    if (!response) {
      setIsDisabled(false);
      return;
    }
    if (response.is_valid) {
      if (environment === InvoicePlanSyscodeString[INVOICE_PLAN_SYSCODE.Sandbox]) newData.course_init_id = response.course_init_id;
      newData.txn_id = txnIdArr.join(',');
      newData.txn_retrieval_ref = retRefArr.join(',');

      beveatApiResourceOwner
        .put(certifyUrl, newData, {
          headers: commonConfigs.resourceOwnerApiHeader,
        })
        .then((res) => {
          try {
            if (res.data.status !== "success") {
              alert("danger");
            } else {
              alert("success");
            }
          } catch (err) {
            console.log(err);
          } finally {
            setIsDisabled(false); // Enable the submit button after API call
            window.location.reload();
          }
        })
        .catch((err) => {
          const err_msg = JSON.stringify(err);
          // const err_msg = JSON.stringify(err.response.data.result.message);
          alert(`Error: ${err_msg}`);
          setIsDisabled(false); // Enable the submit button if there's an error
        });
    } else {
      alert(`${response.message}`);
      setIsDisabled(false);
      return;
    }
  };

  const handleDownloadCertificate = async () => {
    try {
      const certificateUrl = certificateData?.developer_certificate_img_url;

      if (!certificateUrl) {
        alert("Certificate URL not found. Please contact support.");
        return;
      }

      const response = await fetch(certificateUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();

      let filename = 'certificate.pdf'; // fallback
      try {
        const url = new URL(certificateUrl);
        const pathname = url.pathname;
        const urlFilename = pathname.substring(pathname.lastIndexOf('/') + 1);
        if (urlFilename && urlFilename.includes('.pdf')) {
          filename = decodeURIComponent(urlFilename);
        }
      } catch (error) {
        console.warn("Could not extract filename", error);
      }

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(link.href);

    } catch (error) {
      console.error("Error downloading certificate:", error);
      alert("Failed to download certificate. Please try again.");
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-md border border-gray-300">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4 text-left text-black">
          <label
            htmlFor="txn_id"
            className="block text-sm font-medium text-gray-700"
          >
            Transaction ID (From Full-Stack Developer Project) for payment success (
            <span className="inline-flex items-center align-middle  text-green-500">
              <TickCircle size={20} />
            </span>
            )
          </label>
          <input
            type="text"
            name="txn_id"
            id="txn_id"
            disabled={isCertified || isPending || isReleased}
            className={`block w-full pr-10 sm:text-sm rounded-md ${!errors.txn_id
              ? "border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              : "border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500"
              } ${isCertified || isPending || isReleased ? "bg-gray-200 text-gray-700" : "bg-white"
              }`}
            placeholder="Enter txn_id"
            {...register("txn_id", { required: true })}
            style={{ marginBottom: "20px" }}
          />
          {errors.txn_id?.type === "required" && errorIcon}
        </div>
        <div className="mb-4 text-left text-black">
          <label
            htmlFor="txn_retrieval_ref"
            className="block text-sm font-medium text-gray-700"
          >
            Transaction {tab.name} Retrieval Reference (From Full-Stack
            Developer Project) for payment success (
            <span className="inline-flex items-center align-middle  text-green-500">
              <TickCircle size={20} />
            </span>
            ){tab.name === "eNETS (Web)" && <b> : Payment method: eNETS Debit</b>}
          </label>
          <input
            type="text"
            name="txn_retrieval_ref"
            id="txn_retrieval_ref"
            disabled={isCertified || isPending || isReleased}
            className={`block w-full pr-10 sm:text-sm rounded-md ${!errors.txn_retrieval_ref
              ? "border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              : "border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500"
              } ${isCertified || isPending || isReleased
                ? "bg-gray-200 text-gray-700"
                : "bg-white"
              }`}
            placeholder="Enter txn_retrieval_ref"
            {...register("txn_retrieval_ref", { required: true })}
            style={{ marginBottom: "20px" }}
          />
          {errors.txn_retrieval_ref?.type === "required" && errorIcon}
        </div>

        {/* eNETS Additional Payment Success Fields (Pairs 2 & 3) */}
        {tab.name.includes("eNETS") && (
          <>
            {/* Payment Success Pair 2 */}
            <div className="mb-4 text-left text-black">
              <label
                htmlFor="txn_id_2"
                className="block text-sm font-medium text-gray-700"
              >
                Transaction ID (From Full-Stack Developer Project) for payment success (
                <span className="inline-flex items-center align-middle  text-green-500">
                  <TickCircle size={20} />
                </span>
                )
              </label>
              <input
                type="text"
                name="txn_id_2"
                id="txn_id_2"
                disabled={isCertified || isPending || isReleased}
                className={`block w-full pr-10 sm:text-sm rounded-md ${!errors.txn_id_2
                  ? "border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  : "border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500"
                  } ${isCertified || isPending || isReleased ? "bg-gray-200 text-gray-700" : "bg-white"
                  }`}
                placeholder="Enter txn_id"
                {...register("txn_id_2", { required: true })}
                style={{ marginBottom: "20px" }}
              />
              {errors.txn_id_2?.type === "required" && errorIcon}
            </div>
            <div className="mb-4 text-left text-black">
              <label
                htmlFor="txn_retrieval_ref_2"
                className="block text-sm font-medium text-gray-700"
              >
                Transaction {tab.name} Retrieval Reference (From Full-Stack
                Developer Project) for payment success (
                <span className="inline-flex items-center align-middle  text-green-500">
                  <TickCircle size={20} />
                </span>
                ) <b> : Payment method: VISA/MasterCard</b>
              </label>
              <input
                type="text"
                name="txn_retrieval_ref_2"
                id="txn_retrieval_ref_2"
                disabled={isCertified || isPending || isReleased}
                className={`block w-full pr-10 sm:text-sm rounded-md ${!errors.txn_retrieval_ref_2
                  ? "border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  : "border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500"
                  } ${isCertified || isPending || isReleased
                    ? "bg-gray-200 text-gray-700"
                    : "bg-white"
                  }`}
                placeholder="Enter txn_retrieval_ref"
                {...register("txn_retrieval_ref_2", { required: true })}
                style={{ marginBottom: "20px" }}
              />
              {errors.txn_retrieval_ref_2?.type === "required" && errorIcon}
            </div>

            {/* Payment Success Pair 3 */}
            <div className="mb-4 text-left text-black">
              <label
                htmlFor="txn_id_3"
                className="block text-sm font-medium text-gray-700"
              >
                Transaction ID (From Full-Stack Developer Project) for payment success (
                <span className="inline-flex items-center align-middle  text-green-500">
                  <TickCircle size={20} />
                </span>
                )
              </label>
              <input
                type="text"
                name="txn_id_3"
                id="txn_id_3"
                disabled={isCertified || isPending || isReleased}
                className={`block w-full pr-10 sm:text-sm rounded-md ${!errors.txn_id_3
                  ? "border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  : "border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500"
                  } ${isCertified || isPending || isReleased ? "bg-gray-200 text-gray-700" : "bg-white"
                  }`}
                placeholder="Enter txn_id"
                {...register("txn_id_3", { required: true })}
                style={{ marginBottom: "20px" }}
              />
              {errors.txn_id_3?.type === "required" && errorIcon}
            </div>
            <div className="mb-4 text-left text-black">
              <label
                htmlFor="txn_retrieval_ref_3"
                className="block text-sm font-medium text-gray-700"
              >
                Transaction {tab.name} Retrieval Reference (From Full-Stack
                Developer Project) for payment success (
                <span className="inline-flex items-center align-middle  text-green-500">
                  <TickCircle size={20} />
                </span>
                ) <b> : Payment method: eNETS QR</b>
              </label>
              <input
                type="text"
                name="txn_retrieval_ref_3"
                id="txn_retrieval_ref_3"
                disabled={isCertified || isPending || isReleased}
                className={`block w-full pr-10 sm:text-sm rounded-md ${!errors.txn_retrieval_ref_3
                  ? "border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  : "border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500"
                  } ${isCertified || isPending || isReleased
                    ? "bg-gray-200 text-gray-700"
                    : "bg-white"
                  }`}
                placeholder="Enter txn_retrieval_ref"
                {...register("txn_retrieval_ref_3", { required: true })}
                style={{ marginBottom: "20px" }}
              />
              {errors.txn_retrieval_ref_3?.type === "required" && errorIcon}
            </div>
          </>
        )}

        {/* Timeout Fields - NETS QR (1 pair) and eNETS (2 pairs) */}
        {(tab.name.includes("NETS QR") || tab.name.includes("eNETS")) && (
          <>
            <div className="mb-4 text-left text-black">
              <label
                htmlFor="txn_id_timeout"
                className="block text-sm font-medium text-gray-700"
              >
                Transaction ID (From Full-Stack Developer Project) for timeout (
                <span className="inline-flex items-center align-middle  text-red-500">
                  <CloseCircle size={20} />
                </span>
                )
              </label>
              <input
                type="text"
                name="txn_id_timeout"
                id="txn_id_timeout"
                disabled={isCertified || isPending || isReleased}
                className={`block w-full pr-10 sm:text-sm rounded-md ${!errors.txn_id_timeout
                  ? "border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  : "border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500"
                  } ${isCertified || isPending || isReleased ? "bg-gray-200 text-gray-700" : "bg-white"
                  }`}
                placeholder="Enter txn_id"
                {...register("txn_id_timeout", { required: true })}
                style={{ marginBottom: "20px" }}
              />
              {errors.txn_id_timeout?.type === "required" && errorIcon}
            </div>
            <div className="mb-4 text-left text-black">
              <label
                htmlFor="txn_retrieval_ref_timeout"
                className="block text-sm font-medium text-gray-700"
              >
                Transaction {tab.name} Retrieval Reference (From Full-Stack
                Developer Project) for timeout (
                <span className="inline-flex items-center align-middle  text-red-500">
                  <CloseCircle size={20} />
                </span>
                ){tab.name === "eNETS (Web)" && <b> : Payment method: eNETS Debit | VISA/MasterCard</b>}
              </label>
              <input
                type="text"
                name="txn_retrieval_ref_timeout"
                id="txn_retrieval_ref_timeout"
                disabled={isCertified || isPending || isReleased}
                className={`block w-full pr-10 sm:text-sm rounded-md ${!errors.txn_retrieval_ref_timeout
                  ? "border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  : "border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500"
                  } ${isCertified || isPending || isReleased
                    ? "bg-gray-200 text-gray-700"
                    : "bg-white"
                  }`}
                placeholder="Enter txn_retrieval_ref"
                {...register("txn_retrieval_ref_timeout", { required: true })}
                style={{ marginBottom: "20px" }}
              />
              {errors.txn_retrieval_ref_timeout?.type === "required" && errorIcon}
            </div>

            {/* eNETS Additional Timeout Pair */}
            {tab.name.includes("eNETS") && (
              <>
                <div className="mb-4 text-left text-black">
                  <label
                    htmlFor="txn_id_timeout_2"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Transaction ID (From Full-Stack Developer Project) for timeout (
                    <span className="inline-flex items-center align-middle  text-red-500">
                      <CloseCircle size={20} />
                    </span>
                    )
                  </label>
                  <input
                    type="text"
                    name="txn_id_timeout_2"
                    id="txn_id_timeout_2"
                    disabled={isCertified || isPending || isReleased}
                    className={`block w-full pr-10 sm:text-sm rounded-md ${!errors.txn_id_timeout_2
                      ? "border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                      : "border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500"
                      } ${isCertified || isPending || isReleased ? "bg-gray-200 text-gray-700" : "bg-white"
                      }`}
                    placeholder="Enter txn_id"
                    {...register("txn_id_timeout_2", { required: true })}
                    style={{ marginBottom: "20px" }}
                  />
                  {errors.txn_id_timeout_2?.type === "required" && errorIcon}
                </div>
                <div className="mb-4 text-left text-black">
                  <label
                    htmlFor="txn_retrieval_ref_timeout_2"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Transaction {tab.name} Retrieval Reference (From Full-Stack
                    Developer Project) for timeout (
                    <span className="inline-flex items-center align-middle  text-red-500">
                      <CloseCircle size={20} />
                    </span>
                    ) <b> : Payment method: eNETS QR</b>
                  </label>
                  <input
                    type="text"
                    name="txn_retrieval_ref_timeout_2"
                    id="txn_retrieval_ref_timeout_2"
                    disabled={isCertified || isPending || isReleased}
                    className={`block w-full pr-10 sm:text-sm rounded-md ${!errors.txn_retrieval_ref_timeout_2
                      ? "border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                      : "border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500"
                      } ${isCertified || isPending || isReleased
                        ? "bg-gray-200 text-gray-700"
                        : "bg-white"
                      }`}
                    placeholder="Enter txn_retrieval_ref"
                    {...register("txn_retrieval_ref_timeout_2", { required: true })}
                    style={{ marginBottom: "20px" }}
                  />
                  {errors.txn_retrieval_ref_timeout_2?.type === "required" && errorIcon}
                </div>
              </>
            )}
          </>
        )}

        <div className="text-right flex items-center justify-end gap-3">
          {/* Download Certificate Button - Only show for Released status */}
          {isReleased && (
            <button
              type="button"
              onClick={handleDownloadCertificate}
              className="py-2 px-6 rounded-md font-bold bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Download Certificate
            </button>
          )}
          <button
            type="submit"
            disabled={isDisabled || isCertified || isPending || isReleased}
            className={`py-2 px-6 rounded-md font-bold ${isDisabled
              ? "bg-green-600 text-white cursor-not-allowed"
              : isCertified || isReleased
                ? "bg-green-600 text-white cursor-not-allowed"
                : isPending
                  ? "bg-yellow-500 text-white cursor-not-allowed"
                  : "hover:bg-green-500 text-green-500 hover:text-white outline outline-green-500 text-green"
              }`}
            >
            {isDisabled
              ? "Processing..."
              : isCertified || isReleased
                ? "Certified"
                : isPending
                  ? "Pending"
                  : "Certify"}
          </button>
        </div>
      </form>
    </div>
  );
}
